import { NotificationOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Modal, Progress, Row, Statistic, Table, Tag, Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import { BULK_NOTIFICATIONS_MAILING_LISTS } from "../../../../constants/routes";
import { showErrorNotification } from "../../../../utils/notification";
import {
  ACTIVE_DISPATCH_STATES,
  DISPATCH_POLL_INTERVAL_MS,
  DISPATCH_STATE_TAG_COLORS,
  DISPATCH_STATES,
  WAITLIST_API_ENDPOINTS
} from "../../constants";
import { computeDispatchProgressPercent, dispatchProgressStatus } from "../../helper";

const { Text } = Typography;

const DispatchProgressModal = ({
  configId,
  waitlistConfigName,
  ROOT_URL,
  isModalOpen = false,
  setIsModalOpen = () => {}
}) => {
  const [dispatches, setDispatches] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const latestDispatch = dispatches[0];
  // Mirrors `dispatches` so the polling interval can read the latest state without
  // re-registering itself on every fetch.
  const latestDispatchStateRef = useRef(null);

  const fetchDispatches = useCallback(() => {
    setIsFetching(true);
    axios
      .get(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/dispatches`)
      .then((res) => {
        const fetchedDispatches = res.data.dispatches || [];
        setDispatches(fetchedDispatches);
        latestDispatchStateRef.current = fetchedDispatches[0]?.state ?? null;
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [ROOT_URL, configId]);

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isModalOpen || !configId) {
      return;
    }

    fetchDispatches();

    // Poll for progress while the latest known dispatch is still active; stop once
    // it reaches a terminal state (COMPLETED/FAILED) so the modal doesn't keep polling
    // a finished run.
    const intervalId = setInterval(() => {
      if (
        latestDispatchStateRef.current &&
        !ACTIVE_DISPATCH_STATES.includes(latestDispatchStateRef.current)
      ) {
        clearInterval(intervalId);
        return;
      }
      fetchDispatches();
    }, DISPATCH_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isModalOpen, configId, fetchDispatches]);

  // ── Latest dispatch stat cards ─────────────────────────────────────────────

  const renderLatestDispatch = () => {
    if (!latestDispatch) {
      return null;
    }

    const progressPercent = computeDispatchProgressPercent(latestDispatch);
    const isActive = ACTIVE_DISPATCH_STATES.includes(latestDispatch.state);
    const isCompleted = latestDispatch.state === DISPATCH_STATES.COMPLETED;
    const isFailed = latestDispatch.state === DISPATCH_STATES.FAILED;

    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <Text strong style={{ fontSize: 14 }}>
            Latest Dispatch
          </Text>
          <Tag color={DISPATCH_STATE_TAG_COLORS[latestDispatch.state]} style={{ margin: 0 }}>
            {latestDispatch.state}
          </Tag>
        </div>

        {/* Stat cards */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          {[
            { title: "Targeted", value: latestDispatch.targetedEntriesCount ?? 0 },
            {
              title: "Processed",
              value: latestDispatch.processedCount ?? 0,
              valueStyle: { color: "#52c41a" }
            },
            {
              title: "Skipped",
              value: latestDispatch.skippedCount ?? 0,
              valueStyle: { color: "#8c8c8c" }
            }
          ].map(({ title, value, valueStyle = {} }) => (
            <Col key={title} span={8}>
              <div
                style={{
                  background: "rgba(0,0,0,0.02)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 8,
                  padding: "12px 16px"
                }}
              >
                <Statistic
                  title={<span style={{ fontSize: 12 }}>{title}</span>}
                  value={value}
                  valueStyle={{ fontSize: 22, ...valueStyle }}
                />
              </div>
            </Col>
          ))}
        </Row>

        {/* Progress bar */}
        {(isActive || isCompleted || isFailed) && (
          <Progress
            percent={progressPercent}
            status={dispatchProgressStatus(latestDispatch.state)}
            strokeWidth={10}
            format={(pct) =>
              `${pct}% · ${
                (latestDispatch.processedCount ?? 0) + (latestDispatch.skippedCount ?? 0)
              } / ${latestDispatch.targetedEntriesCount ?? 0}`
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Failure alert */}
        {isFailed && latestDispatch.failureReason && (
          <Alert
            type="error"
            showIcon
            message="Dispatch failed"
            description={latestDispatch.failureReason}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Completed CTA */}
        {isCompleted && (
          <div style={{ marginBottom: 16 }}>
            <Button
              icon={<NotificationOutlined />}
              onClick={() => window.open(BULK_NOTIFICATIONS_MAILING_LISTS, "_blank")}
            >
              Announce via Bulk Notifications
            </Button>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {waitlistConfigName ? `"${waitlistConfigName}"` : "This waitlist"} already has a
                dedicated mailing list with these members — send a campaign to it from Bulk
                Notifications.
              </Text>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── History table ──────────────────────────────────────────────────────────

  const historyColumns = [
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 130,
      render: (state) => <Tag color={DISPATCH_STATE_TAG_COLORS[state]}>{state}</Tag>
    },
    {
      title: "Targeted",
      dataIndex: "targetedEntriesCount",
      key: "targetedEntriesCount",
      width: 90
    },
    {
      title: "Processed",
      dataIndex: "processedCount",
      key: "processedCount",
      width: 90
    },
    {
      title: "Skipped",
      dataIndex: "skippedCount",
      key: "skippedCount",
      width: 90
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {createdAt ? dayjs(createdAt).format("DD MMM YYYY | HH:mm") : "--"}
        </Text>
      )
    },
    {
      title: "Finished At",
      dataIndex: "finishedAt",
      key: "finishedAt",
      render: (finishedAt) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {finishedAt ? dayjs(finishedAt).format("DD MMM YYYY | HH:mm") : "--"}
        </Text>
      )
    }
  ];

  return (
    <Modal
      title="Dispatch Progress"
      open={isModalOpen}
      footer={null}
      onCancel={() => setIsModalOpen(false)}
      width={900}
    >
      {renderLatestDispatch()}

      <Table
        title={() => (
          <Text strong style={{ fontSize: 13 }}>
            Dispatch History
          </Text>
        )}
        columns={historyColumns}
        dataSource={dispatches}
        rowKey="_id"
        loading={isFetching}
        bordered
        pagination={false}
        size="small"
      />
    </Modal>
  );
};

export default DispatchProgressModal;
